import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Check, FileText, Calculator, Sparkles, Receipt, Percent, Users, Minus, Plus, PieChart, Upload, PiggyBank, Wallet } from "lucide-react";

const FAQS = [
  {
    q: "How is 'Tax to set aside' calculated?",
    a: "It's computed on your Financial Year (FY)-to-date income only — no future projection. Income tax is the cheaper of (New regime after ₹75,000 standard deduction) or (Old regime after ₹50,000 standard deduction) applied to what you've actually earned so far. Plus outstanding Goods & Services Tax (GST) liability (output GST on issued invoices − Input Tax Credit (ITC) from expenses). As you log more income, the number grows naturally.",
  },
  {
    q: "Goods & Services Tax (GST) is payable on invoices I've raised but not collected?",
    a: "Yes — Indian GST law (Section 12 & 13 of the CGST Act) recognises liability at the time of supply, which is invoice issuance. So every non-draft, non-cancelled invoice contributes GST to your payable the moment it's sent, even if the client hasn't paid. Drafts and cancelled invoices are excluded. This is why the Dashboard's 'Total Income' is shown on accrual basis — what you've earned, not just banked.",
  },
  {
    q: "Do I need to be Goods & Services Tax (GST) registered to use it?",
    a: "No. FinCanvas works for pre-GST freelancers too — you'll see a GST-threshold tracker that tells you how close you are to the ₹20 Lakh limit and when to register. If you do cross the threshold and haven't registered, we'll flag it on the dashboard with a countdown.",
  },
  {
    q: "Can clients pay invoices online?",
    a: "Yes. Connect Razorpay in Settings → Payment Gateways (free test keys work) and every invoice gets a 'Pay Now' button backed by Unified Payments Interface (UPI) + card + netbanking. The invoice auto-marks paid when Razorpay webhooks fire. Stripe support is coming next for United States Dollar (USD) and international invoices.",
  },
  {
    q: "How does the Gmail sync work? Is it safe?",
    a: "We use Google's official Open Authorization (OAuth) with the gmail.readonly scope — we only read emails, never send or modify anything. The sync scans the last 30 days for payout emails from YouTube, Stripe, Razorpay, Patreon, Substack, PayPal and 12+ other sources, uses Artificial Intelligence (Claude) to extract the amount / currency / date, and auto-dedupes against bank credits so you don't double-count. You can disconnect anytime in Settings — we revoke the token at Google's end.",
  },
  {
    q: "What if my bank doesn't send emails for credits?",
    a: "Many don't by default, but most Indian banks (HDFC, ICICI, SBI, Axis, Kotak) support opt-in email alerts — turn them on once in netbanking and our Gmail sync picks them up. In the meantime you can log credits manually in 3 taps, import a bank-statement Comma-Separated Values (CSV) or Excel file, or use the 'Paste from email' AI parser.",
  },
  {
    q: "What about data privacy? Where's my data stored?",
    a: "Your data lives in our MongoDB cluster hosted in India. Passwords are bcrypt-hashed. Application Programming Interface (API) keys (Razorpay, Gmail tokens) are stored encrypted with Fernet symmetric encryption. We don't sell or share data — the AI co-pilot uses Anthropic / Google APIs which don't train on your messages. You can delete your account anytime and we'll wipe everything within 7 days.",
  },
  {
    q: "Is FinCanvas built for Indian freelancers?",
    a: "Yes — every calculation is rooted in Indian tax law. GST thresholds (₹20 Lakh / ₹10 Lakh for special-category states), Central GST (CGST) / State GST (SGST) vs Integrated GST (IGST) splits, advance-tax milestones under Section 208 of the Income Tax Act, FY 2025–26 income-tax slabs for both Old and New regime, and standard deductions (₹50,000 Old, ₹75,000 New) are all built in. If you're outside India, it'll still work for tracking income — but the tax numbers won't match your jurisdiction.",
  },
  {
    q: "Can I export my data to share with my Chartered Accountant (CA)?",
    a: "Yes. Invoices have Portable Document Format (PDF) downloads, and every page — Income, Expenses, GST, Income Tax — has a CSV / JSON export. GSTR-1 / GSTR-3B ready exports are on the roadmap.",
  },
  {
    q: "Is there a free plan?",
    a: "Right now, everything is free while we're in early access. When we introduce pricing, it'll start at ₹399 per month — founding users get a lifetime 50% discount locked in, plus unlimited invoices from day one.",
  },
];

export default function Landing() {
  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 72; // offset for sticky header
      window.scrollTo({ top, behavior: "smooth" });
      // Update the URL hash without jump
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf8] text-[#0a0a0a]">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#e5e5e0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <span className="block w-2 h-2 bg-[#d95d39]" />
            <span className="font-['Outfit'] text-xl font-bold tracking-tight">FinCanvas</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#5a5a53]">
            <a href="#features" onClick={scrollToSection("features")} className="hover:text-[#0a0a0a] cursor-pointer" data-testid="nav-features">Features</a>
            <a href="#pricing" onClick={scrollToSection("pricing")} className="hover:text-[#0a0a0a] cursor-pointer" data-testid="nav-pricing">Pricing</a>
            <a href="#faq" onClick={scrollToSection("faq")} className="hover:text-[#0a0a0a] cursor-pointer" data-testid="nav-faq">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login-link">
              <Button variant="ghost" className="text-sm">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="landing-register-link">
              <Button className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white text-sm rounded-md">
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-[0.45]"
          style={{
            backgroundImage:
              "url(https://static.prod-images.emergentagent.com/jobs/66a04db3-3c9c-4b28-89b5-dc26d4f1a273/images/57e0f7312a1965de3e79a056df2dc1f364b86ebbbcf9b75281b4219d36e25b11.png)",
            backgroundSize: "cover",
            backgroundPosition: "right center",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-32 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8 tc-fade-up">
            <p className="tc-overline mb-6">The financial dashboard for freelancers</p>
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
              What came in.
              <br />
              What went out.
              <br />
              What you owe.
              <br />
              <span className="text-[#d95d39]">Done.</span>
            </h1>
            <p className="mt-8 text-lg text-[#5a5a53] max-w-xl leading-relaxed">
              FinCanvas quietly handles invoices, income, expenses, Goods &amp; Services Tax (GST),
              income tax and an Artificial Intelligence (AI) co-pilot — all in one place.
              You stay focused on the work that pays you; we keep the books, returns and reminders running.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta-register">
                <Button className="bg-[#d95d39] hover:bg-[#c84c35] text-white px-7 py-6 rounded-md text-base font-medium">
                  Start your canvas <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-login">
                <Button
                  variant="outline"
                  className="border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white px-7 py-6 rounded-md text-base"
                >
                  See the demo
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#5a5a53]">
              {["Invoices & payments", "Income & expense tracking", "GST + income tax", "AI co-pilot"].map((t) => (
                <span key={t} className="flex items-center gap-2">
                  <Check size={14} className="text-[#4a6b53]" /> {t}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-4 tc-fade-up">
            <div className="tc-card p-8 bg-white relative overflow-hidden">
              <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#4a6b53] animate-pulse" />
              <p className="tc-overline mb-3">Live preview</p>
              <div className="font-['Outfit'] text-3xl font-bold">₹4,82,500</div>
              <div className="text-xs text-[#5a5a53] mt-1">collected this quarter</div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <Mini label="Pending" value="₹1,12,000" tone="warn" />
                <Mini label="GST due" value="₹38,250" tone="bad" />
                <Mini label="Spent" value="₹1,61,500" tone="ink" />
                <Mini label="Tax reserve" value="₹68,400" tone="good" />
              </div>
              <div className="mt-6 pt-4 border-t border-[#e5e5e0] text-xs text-[#5a5a53]">
                Top spend: <span className="font-bold text-[#0a0a0a]">Software</span> ₹42K · <span className="font-bold text-[#0a0a0a]">Travel</span> ₹28K · <span className="font-bold text-[#0a0a0a]">Rent</span> ₹54K
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="bg-white border-y border-[#e5e5e0]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <p className="tc-overline mb-4">What's inside</p>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold tracking-tight mb-6 max-w-2xl">
            One workspace.
            <br />Every money question.
          </h2>
          <p className="text-base md:text-lg text-[#5a5a53] mb-16 max-w-2xl leading-relaxed">
            Most freelancers juggle three apps for invoicing, a fourth for taxes, and a spreadsheet for spending.
            FinCanvas folds it all into a single, calm financial dashboard.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-[#e5e5e0]">
            <Feat span="md:col-span-7" icon={Wallet} title="Live financial dashboard"
              desc="Total income, pending collections, GST payable, tax to set aside — all rolled up. Open the app, see exactly where you stand in 5 seconds." />
            <Feat span="md:col-span-5" icon={PieChart} title="Where your money goes"
              desc="Auto-categorised expense tracking. See your top spend categories, biggest vendors and a 6-month trend — without touching a spreadsheet." />
            <Feat span="md:col-span-5" icon={Upload} title="Bank statement import"
              desc="Drop your bank's CSV / Excel. We auto-detect credit vs debit columns, classify each row, and create income / expense entries instantly." />
            <Feat span="md:col-span-7" icon={FileText} title="Goods &amp; Services Tax (GST) ready invoices"
              desc="Central GST (CGST) / State GST (SGST) or Integrated GST (IGST) splits handled automatically. PDF download with your branding. Razorpay 'Pay Now' link on every invoice." />
            <Feat span="md:col-span-7" icon={Calculator} title="Income tax computation — Old vs New regime"
              desc="Updated for Financial Year (FY) 2025–26. Plug in 80C, 80D, National Pension System (NPS), home-loan. Get the cheaper regime instantly, plus an advance-tax schedule for June, September, December, March." />
            <Feat span="md:col-span-5" icon={Percent} title="GST liability tracker"
              desc="Output tax, input tax credit (ITC), and net payable — month by month. Always GSTR-1 / GSTR-3B ready." />
            <Feat span="md:col-span-5" icon={PiggyBank} title="Tax to set aside"
              desc="Know exactly how much cash to park right now so advance-tax + GST instalments don't scramble your runway. Updates every time you log income." />
            <Feat span="md:col-span-7" icon={Sparkles} title="AI co-pilot trained for India"
              desc="Ask anything: GST thresholds, Section 44ADA, presumptive tax, e-invoicing, ITC reversals, expense categorisation. Powered by Claude Sonnet 4.5." />
            <Feat span="md:col-span-12" icon={Users} title="Client memory + Gmail income sync"
              desc="Save GSTINs, billing addresses, payment history. Connect Gmail (read-only) and we'll auto-import payouts from YouTube, Stripe, Razorpay, PayPal, Patreon, Substack and 12+ other sources — deduplicated against bank credits so you don't double-count." />
          </div>
        </div>
      </section>

      {/* Persona */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div
          className="aspect-[4/5] rounded-md overflow-hidden border border-[#e5e5e0]"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1758366278819-413b166ca7c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxmcmVlbGFuY2VyJTIwY3JlYXRvciUyMG1vZGVybiUyMHdvcmtzcGFjZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzY1NTc0Nnww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div>
          <p className="tc-overline mb-4">Made for solo earners</p>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
            Designers, devs, consultants,
            <br />photographers, coaches — we get you.
          </h2>
          <p className="mt-6 text-lg text-[#5a5a53] leading-relaxed">
            You didn't go solo to wrestle with TallyPrime or Excel sheets. FinCanvas
            handles your whole financial back-office — income, expenses, taxes,
            invoices — so you can ship the work that pays you.
          </p>
          <div className="mt-8 space-y-3">
            {[
              "Live income, expense & tax in one view",
              "Bank statement import — auto-classified",
              "Quarterly advance-tax reminders",
              "Indian Rupee (INR) computation that's actually correct",
              "PDF invoices that look like you designed them",
              "AI that speaks Indian tax fluently",
            ].map((t) => (
              <div key={t} className="flex items-center gap-3 text-sm">
                <span className="block w-1.5 h-1.5 bg-[#d95d39]" />
                <span className="text-[#0a0a0a]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-[#fafaf8] border-y border-[#e5e5e0]">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="mb-14">
            <p className="tc-overline mb-3" style={{ color: "#d95d39" }}>Questions, answered</p>
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold tracking-tight text-[#0a0a0a]">
              Things freelancers
              <br />
              keep asking us.
            </h2>
          </div>
          <div className="divide-y divide-[#e5e5e0] border-y border-[#e5e5e0]" data-testid="landing-faq">
            {FAQS.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} idx={i} />
            ))}
          </div>
          <p className="mt-10 text-sm text-[#5a5a53]">
            Still stuck? Email <a href="mailto:fincanvashelp@gmail.com" className="text-[#d95d39] hover:underline font-medium">fincanvashelp@gmail.com</a> — we reply within a working day.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="bg-[#0a0a0a] text-[#fafaf8]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <p className="tc-overline mb-4" style={{ color: "#d95d39" }}>The deal</p>
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold tracking-tight">
              Free while we cook.
              <br />
              <span className="text-[#d95d39]">Starting ₹399/month when we're done.</span>
            </h2>
            <p className="mt-6 text-[#a3a39b] max-w-lg leading-relaxed">
              Early users get unlimited invoices, AI co-pilot, expense analytics,
              GST + income tax trackers, bank statement import, and a lifetime 50% discount when we go paid.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end items-end">
            <Link to="/register" data-testid="bottom-cta-register">
              <Button className="bg-[#d95d39] hover:bg-[#c84c35] text-white px-8 py-7 rounded-md text-base font-medium">
                Claim early access <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#0a0a0a] text-[#5a5a53] border-t border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2 text-[#fafaf8]">
            <span className="block w-2 h-2 bg-[#d95d39]" />
            <span className="font-['Outfit'] font-bold">FinCanvas</span>
            <span className="text-[#5a5a53] ml-2">© 2026</span>
          </div>
          <div className="text-[#5a5a53]">Crafted in India for the freelance economy.</div>
        </div>
      </footer>
    </div>
  );
}

function Mini({ label, value, tone }) {
  const tones = {
    warn: "text-[#d4b872]",
    bad: "text-[#b83a26]",
    good: "text-[#4a6b53]",
    ink: "text-[#0a0a0a]",
  };
  return (
    <div className="border border-[#e5e5e0] rounded-md p-3">
      <div className="text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">{label}</div>
      <div className={`font-['Outfit'] text-lg font-bold mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function Feat({ icon: Icon, title, desc, span }) {
  return (
    <div className={`bg-white p-8 ${span}`}>
      <Icon size={20} strokeWidth={1.5} className="text-[#d95d39]" />
      <h3 className="mt-4 font-['Outfit'] text-2xl font-medium tracking-tight text-[#0a0a0a]">
        {title}
      </h3>
      <p className="mt-3 text-[#5a5a53] text-sm leading-relaxed max-w-md">{desc}</p>
    </div>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="py-5" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 text-left group"
        aria-expanded={open}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-['Outfit'] text-lg md:text-xl font-medium tracking-tight text-[#0a0a0a] group-hover:text-[#d95d39] transition-colors">
          {q}
        </span>
        <span className={`shrink-0 mt-1 w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${
          open ? "bg-[#d95d39] border-[#d95d39] text-white" : "border-[#e5e5e0] text-[#5a5a53] group-hover:border-[#d95d39] group-hover:text-[#d95d39]"
        }`}>
          {open ? <Minus size={14} /> : <Plus size={14} />}
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <p className="text-[#5a5a53] text-sm leading-relaxed max-w-3xl">{a}</p>
        </div>
      </div>
    </div>
  );
}
